import { getPerms } from '@/lib/perms';
import { getQueryClient } from '@/providers/queryClient';
import { getList } from '@/requests/api/get-list';
import { QUERY_KEYS } from '@/requests/constants';
import type {
  Article,
  BaseInstance,
  ModelName,
  RelationalFieldValue,
} from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

interface BaseSpace extends BaseInstance {
  id: string;
}

interface BaseRevenueRow extends BaseInstance {
  space?: RelationalFieldValue<BaseSpace>;
  article?: RelationalFieldValue<Article>;
}

/**
 * Creates a generic handler for fetching and filtering revenue rows for different space types
 * @param revenueRowModelName The model name for the revenue rows (e.g., 'apartmentrevenuerow')
 * @returns A handler function that processes the revenue rows for the given space type
 */
export function createBaseRentHandler<
  TSpace extends BaseSpace,
  TRevenueRow extends BaseRevenueRow,
>(revenueRowModelName: ModelName) {
  return async function handleCustomNestedCase(
    listResponse: TCustomList<TSpace>
  ): Promise<TCustomList<TSpace & { revenueRows?: TRevenueRow[] }>> {
    // check if perms to see revenue rows
    const { canView } = getPerms(revenueRowModelName);

    // Early return if no data
    if (!listResponse.data.length || !canView) return listResponse;

    // Get unique IDs efficiently
    const idsSet = new Set(listResponse.data.map((space) => space.id));
    if (idsSet.size === 0) return listResponse;

    // Calculate dates once
    const now = DateTime.now();
    const endOfMonth = now.endOf('month').toISODate();
    const startOfMonth = now.startOf('month').toISODate();

    // Fetch revenue rows
    const chunkedSpaceIds = chunkIds(idsSet);
    const resolvedRevenueRows = await fetchRevenueRows<TRevenueRow>(
      chunkedSpaceIds,
      revenueRowModelName,
      endOfMonth,
      startOfMonth
    );

    if (!resolvedRevenueRows.length) {
      return {
        data: listResponse.data.map((space) => ({ ...space, revenueRows: [] })),
      };
    }

    // Get unique article IDs efficiently
    const articleIdsSet = new Set(
      resolvedRevenueRows
        .map((row: TRevenueRow) => row.article?.id)
        .filter((id) => id != null)
    );

    // Fetch and filter articles
    const filteredRevenueRows = articleIdsSet.size
      ? await filterRevenueRowsByArticles(resolvedRevenueRows, articleIdsSet)
      : resolvedRevenueRows;

    // Create an index for faster lookups
    const revenueRowsBySpaceId = filteredRevenueRows.reduce<
      Record<string, TRevenueRow[]>
    >((acc, row: TRevenueRow) => {
      const spaceId = row.space?.id;
      if (spaceId) {
        if (!acc[spaceId]) acc[spaceId] = [];
        acc[spaceId].push(row);
      }
      return acc;
    }, {});

    // Map spaces with their revenue rows using the index
    return {
      data: listResponse.data.map((space) => ({
        ...space,
        revenueRows: revenueRowsBySpaceId[space.id] || [],
      })),
    };
  };
}

async function fetchRevenueRows<TRevenueRow extends BaseInstance>(
  chunkedIds: string[][],
  modelName: ModelName,
  endOfMonth: string,
  startOfMonth: string
): Promise<TRevenueRow[]> {
  const queryClient = getQueryClient();

  const promises = chunkedIds.map(async (chunk) => {
    return queryClient.ensureQueryData({
      queryKey: [
        modelName,
        QUERY_KEYS.LIST,
        {
          spaceIds: chunk,
          startDate: startOfMonth,
          endDate: endOfMonth,
        },
      ],
      queryFn: () =>
        getList<TRevenueRow>({
          modelName,
          queryParams: {
            filters: {
              space: {
                __in: chunk.join(','),
              },
              start_date: {
                '__gt!': endOfMonth,
              },
              end_date: {
                '__lt!': startOfMonth,
              },
              automatic_distribution_quota: {
                __isnull: true,
              },
              space_renovation_notation: {
                __isnull: true,
              },
              structure_renovation_notation: {
                __isnull: true,
              },
            },
            pageSize: 500,
            order: ['-createdAt'],
          },
        }),
    });
  });

  const results = await Promise.allSettled(promises);
  return results.reduce<TRevenueRow[]>((acc, result) => {
    if (result.status === 'fulfilled' && result.value.list) {
      acc.push(...result.value.list);
    }
    return acc;
  }, []);
}

async function filterRevenueRowsByArticles<TRevenueRow extends BaseRevenueRow>(
  revenueRows: TRevenueRow[],
  articleIds: Set<string>
): Promise<TRevenueRow[]> {
  const queryClient = getQueryClient();
  const chunkedArticleIds = chunkIds(articleIds);

  const promises = chunkedArticleIds.map(async (chunk) => {
    return queryClient.ensureQueryData({
      queryKey: [
        'article',
        QUERY_KEYS.LIST,
        {
          ids: chunk,
          considerBaseRent: true,
        },
      ],
      queryFn: () =>
        getList<Article>({
          modelName: 'article',
          queryParams: {
            filters: {
              id: {
                __in: chunk.join(','),
              },
              consider_base_rent: {
                noop: true,
              },
            },
          },
        }),
    });
  });

  const results = await Promise.allSettled(promises);
  const validArticles = new Set(
    results.reduce<string[]>((acc, result) => {
      if (result.status === 'fulfilled' && result.value.list) {
        acc.push(...result.value.list.map((article) => article.id));
      }
      return acc;
    }, [])
  );

  return revenueRows.filter(
    (revenueRow) =>
      revenueRow.article?.id && validArticles.has(revenueRow.article.id)
  );
}
